import {
  Attribute,
  JsonApiModel,
  HasMany,
  JsonApiModelConfig,
} from '@elearnio/angular2-jsonapi';

import { Member } from '@models/member.model';

@JsonApiModelConfig({
  type: 'company_settings',
})
export class CompanySetting extends JsonApiModel {
  @Attribute()
  email: string | undefined;

  @Attribute()
  slug: string | undefined;

  @Attribute()
  subdomain: string | undefined;

  @Attribute({ serializedName: 'address_city' })
  addressCity!: string;

  @Attribute({ serializedName: 'address_street' })
  addressStreet!: string;

  @Attribute({ serializedName: 'address_zipcode' })
  addressZipcode!: string;

  @Attribute({ serializedName: 'brand_color' })
  brandColor!: string;

  @Attribute({ serializedName: 'interactive_elements_color' })
  interactiveElementsColor!: string;

  @Attribute({ serializedName: 'main_button_color' })
  mainButtonColor!: string;

  @Attribute({ serializedName: 'button_text_color' })
  buttonTextColor!: string;

  @Attribute({ serializedName: 'linked_in' })
  linkedIn!: string;

  @Attribute()
  logo!: string;

  @Attribute()
  favicon!: string;

  @Attribute()
  name!: string;

  @Attribute({ serializedName: 'net_time_enabled' })
  enableIDDTime!: boolean;

  @Attribute({ serializedName: 'email_enabled' })
  emailEnabled!: boolean;

  @Attribute({ serializedName: 'sender_email_address' })
  senderEmailAddress!: string;

  @Attribute({ serializedName: 'sender_email_display_name' })
  senderEmailDisplayName!: string;

  @Attribute({ serializedName: 'reply_to_email_address' })
  replyToEmailAddress!: string;

  @Attribute()
  consent!: boolean;

  @Attribute({ serializedName: 'time_zone' })
  timeZone!: string;

  @Attribute({ serializedName: 'default_language' })
  defaultLanguage!: string;

  // The company's active content languages — the single source of truth for which languages appear
  // in every language selector platform-wide. Writable by admins on the languages settings page.
  @Attribute({ serializedName: 'enabled_languages' })
  enabledLanguages: string[] | undefined;

  // All platform-supported languages (read-only) — the dynamic eligibility list the languages
  // settings page renders as activatable toggles.
  @Attribute({ serializedName: 'available_languages' })
  availableLanguages: string[] | undefined;

  // Per-language content counts (read-only) — drives the "in use" warning before deactivation.
  @Attribute({ serializedName: 'language_usage' })
  languageUsage: Record<string, number> | undefined;

  @Attribute()
  website!: string;

  @Attribute({ serializedName: 'imprint_url' })
  imprintUrl!: string;

  @Attribute({ serializedName: 'privacy_policy_url' })
  privacyPolicyUrl!: string;

  @Attribute({ serializedName: 'show_gamification_ranking' })
  showGamificationRanking!: boolean;

  @Attribute({ serializedName: 'show_gamification_badge' })
  showGamificationBadge!: boolean;

  @Attribute({ serializedName: 'anonymous_gamification_ranking' })
  anonymousGamificationRanking!: boolean;

  @Attribute({ serializedName: 'enable_oauth' })
  enableOauth!: boolean;

  @Attribute({ serializedName: 'orga_enabled' })
  enableOrga!: boolean;

  @Attribute({ serializedName: 'orga_tree' })
  orgaTree!: any;

  @Attribute({ serializedName: 'ai_enabled' })
  enableAI!: boolean;

  @Attribute({ serializedName: 'username_login' })
  enableLoginUsername!: boolean;

  @Attribute({ serializedName: 'default_password' })
  defaultPassword!: string;

  @Attribute({ serializedName: 'channel_enabled' })
  channelEnabled!: boolean;

  @Attribute({ serializedName: 'google_enabled' })
  googleEnabled!: boolean;

  @Attribute({ serializedName: 'ms_teams_integration' })
  msTeamsIntegration!: any;

  @Attribute({ serializedName: 'google_integration' })
  googleIntegration!: any;

  @Attribute({ serializedName: 'zoom_integration' })
  zoomIntegration!: any;

  @Attribute({ serializedName: 'onboarding_duration_days' })
  onboardingDurationDays!: number;

  @Attribute({ serializedName: 'publishing_approval_mode' })
  publishingApprovalMode: string | undefined;

  @Attribute({ serializedName: 'publishing_approval_reviewer_admin' })
  publishingApprovalReviewerAdmin: boolean | undefined;

  @Attribute({ serializedName: 'publishing_approval_reviewer_global_coach' })
  publishingApprovalReviewerGlobalCoach: boolean | undefined;

  @Attribute({ serializedName: 'reviewer_user_ids' })
  reviewerUserIds: string[] | undefined;

  @Attribute({ serializedName: 'pending_publishing_approval_count' })
  pendingPublishingApprovalCount: number | undefined;

  @Attribute({ serializedName: 'preboarding_email_enabled' })
  preboardingEmailEnabled!: boolean;

  @HasMany()
  members!: Member[];
}
